import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Wrench, ShieldCheck, Timer, SearchCheck, ArrowRight, ArrowLeft, Upload, CheckCircle2, Smartphone, Laptop, Monitor, Gamepad2, Cpu } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { useContent, parseList, parseLines } from "@/lib/content";

const BADGE_ICONS = [Timer, ShieldCheck, SearchCheck];

const DEVICE_TYPES = [
  { id: "Smartphone", icon: Smartphone, base: 79 },
  { id: "Laptop", icon: Laptop, base: 99 },
  { id: "Desktop PC", icon: Monitor, base: 99 },
  { id: "Game Console", icon: Gamepad2, base: 89 },
  { id: "Other / Retro", icon: Cpu, base: 69 },
];

const ISSUES = ["Cracked screen", "Battery drains fast", "Won't power on", "Charging port loose", "Overheating / loud fans",
  "Liquid damage", "HDMI / display output", "Running slow / virus", "Data recovery needed", "Cosmetic / deep clean"];

export default function Repair() {
  const [step, setStep] = useState(1);
  const [device, setDevice] = useState(null);
  const [model, setModel] = useState("");
  const [issues, setIssues] = useState([]);
  const [desc, setDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [trackId, setTrackId] = useState("");
  const [tracked, setTracked] = useState(null);
  const content = useContent();
  const menu = parseList(content["repair.menu"]).map((r) => ({ s: r[0], p: r[1], t: r[2] }));
  const rh1 = (content["repair.h1"] || "").split("|");
  const badges = parseLines(content["repair.badges"]);

  const estimate = useMemo(() => {
    if (!device) return 0;
    return device.base + Math.max(0, issues.length - 1) * 15;
  }, [device, issues]);

  const toggleIssue = (i) => setIssues(issues.includes(i) ? issues.filter((x) => x !== i) : [...issues, i]);

  const submit = async () => {
    if (!contact.name || !contact.email) return toast.error("Name and email are required.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/submissions/repair-quote", {
        ...contact, device_type: device.id, brand_model: model, issues,
        description: desc, estimate, file_names: files,
      });
      setResult(data);
      toast.success("Quote request received! Check your email for confirmation.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const track = async () => {
    setTracked(null);
    try {
      const { data } = await api.get(`/repairs/track/${trackId.trim()}`);
      setTracked(data);
    } catch {
      toast.error("No repair found with that reference ID.");
    }
  };

  return (
    <div data-testid="repair-page">
      <section className="relative overflow-hidden border-b border-white/10">
        <img src={content["images.repair_hero"]} alt="Micro-soldering repair" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/40" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-neon-gold">{content["repair.eyebrow"]}</p>
          <h1 className="max-w-3xl font-display text-4xl font-black uppercase tracking-tight text-white sm:text-6xl" data-testid="repair-heading">
            {rh1[0]} <span className="text-neon-gold glow-gold">{rh1[1]}</span> {rh1[2]}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
            {content["repair.intro"]}
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {badges.map((t, i) => {
              const Icon = BADGE_ICONS[i] || ShieldCheck;
              return (
                <span key={t} className="flex items-center gap-2 rounded-full border border-neon-gold/30 bg-neon-gold/5 px-4 py-2 font-mono text-xs text-neon-gold">
                  <Icon className="h-4 w-4" /> {t}
                </span>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE MENU */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6" data-testid="repair-menu-section">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <h2 className="mb-8 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{content["repair.menu_h"]}</h2>
            <div className="overflow-hidden rounded-xl border border-white/10">
              {menu.map((m, i) => (
                <div key={m.s} data-testid={`repair-menu-row-${i}`}
                  className={`flex flex-wrap items-center justify-between gap-2 px-5 py-4 ${i % 2 ? "bg-inkalt/60" : "bg-inkalt/30"}`}>
                  <p className="text-sm text-slate-200">{m.s}</p>
                  <div className="flex items-center gap-6 font-mono text-xs">
                    <span className="text-neon-gold">{m.p}</span>
                    <span className="text-slate-400">{m.t}</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 font-mono text-xs text-slate-500">{content["repair.warranty_note"]}</p>
          </div>
          <div className="lg:col-span-2">
            <div className="crt relative overflow-hidden rounded-xl border border-white/10">
              <img src={content["images.repair_solder"]} alt="Circuit board repair" loading="lazy" className="h-64 w-full object-cover opacity-80" />
            </div>
            <div className="glass mt-5 rounded-xl p-6" data-testid="repair-track-widget">
              <h3 className="mb-1 font-display text-base font-extrabold uppercase text-white">{content["repair.track_h"]}</h3>
              <p className="mb-4 text-xs text-slate-400">{content["repair.track_text"]}</p>
              <div className="flex gap-2">
                <Input value={trackId} onChange={(e) => setTrackId(e.target.value)} data-testid="repair-track-input"
                  placeholder="e.g. 3fa85f64-..." className="border-white/10 bg-ink font-mono text-xs text-white" />
                <button onClick={track} data-testid="repair-track-button"
                  className="shrink-0 rounded-md bg-neon-gold px-4 font-display text-xs font-extrabold uppercase text-ink transition-colors hover:bg-white">
                  Track
                </button>
              </div>
              {tracked && (
                <div className="mt-4 rounded-md border border-neon-cyan/30 bg-neon-cyan/5 p-3 font-mono text-xs" data-testid="repair-track-result">
                  <p className="text-slate-300">Device: <span className="text-white">{tracked.device || "—"}</span></p>
                  <p className="mt-1 text-slate-300">Status: <span className="uppercase text-neon-cyan">{tracked.status}</span></p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE WIZARD */}
      <section id="quote-form" className="scroll-mt-24 border-t border-white/10 bg-inkalt/40" data-testid="quote-section">
        <div className="mx-auto max-w-4xl px-4 py-20 sm:px-6">
          <p className="mb-2 font-mono text-xs uppercase tracking-[0.35em] text-neon-gold">{content["repair.quote_eyebrow"]}</p>
          <h2 className="mb-10 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">{content["repair.quote_h"]}</h2>

          {result ? (
            <div className="glass rounded-xl p-10 text-center" data-testid="repair-quote-success">
              <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-neon-green" />
              <h3 className="font-display text-2xl font-extrabold uppercase text-white">Quote Requested</h3>
              <p className="mt-2 text-sm text-slate-400">Reference ID — save this to track your repair:</p>
              <p className="mt-2 select-all rounded-md bg-ink px-4 py-2 font-mono text-sm text-neon-cyan" data-testid="repair-quote-reference">{result.id}</p>
              <p className="mt-4 text-sm text-slate-400">We'll email {result && contact.email} within 1 business day with your confirmed quote.</p>
            </div>
          ) : (
            <div className="glass rounded-xl p-6 sm:p-10">
              <div className="mb-8 flex items-center gap-2" data-testid="quote-steps">
                {[1, 2, 3].map((s) => (
                  <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${step >= s ? "bg-neon-gold" : "bg-white/10"}`} />
                ))}
              </div>

              {step === 1 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <p className="mb-4 font-mono text-xs uppercase tracking-widest text-slate-400">Step 1 · What's broken?</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {DEVICE_TYPES.map((d) => (
                      <button key={d.id} data-testid={`device-type-${d.id.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => setDevice(d)}
                        className={`flex flex-col items-center gap-2 rounded-lg border p-5 transition-colors ${device?.id === d.id ? "border-neon-gold bg-neon-gold/10 text-neon-gold" : "border-white/10 text-slate-300 hover:border-neon-gold/50"}`}>
                        <d.icon className="h-7 w-7" />
                        <span className="font-display text-xs font-bold uppercase">{d.id}</span>
                        <span className="font-mono text-[10px] text-slate-500">from ${d.base}</span>
                      </button>
                    ))}
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button onClick={() => device ? setStep(2) : toast.error("Pick a device type first.")} data-testid="quote-step1-next-button"
                      className="inline-flex items-center gap-2 rounded-md bg-neon-gold px-6 py-3 font-display text-xs font-extrabold uppercase text-ink transition-colors hover:bg-white">
                      Next <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <p className="mb-4 font-mono text-xs uppercase tracking-widest text-slate-400">Step 2 · Details & symptoms</p>
                  <Label htmlFor="brand-model" className="text-xs text-slate-400">Brand & model *</Label>
                  <Input id="brand-model" value={model} onChange={(e) => setModel(e.target.value)} data-testid="quote-model-input"
                    placeholder="e.g. iPhone 14 Pro, PS5, 2019 MacBook Pro" className="mb-4 border-white/10 bg-ink text-white" />
                  <p className="mb-2 text-xs text-slate-400">Select all that apply:</p>
                  <div className="mb-4 flex flex-wrap gap-2">
                    {ISSUES.map((i) => (
                      <button key={i} data-testid={`issue-${i.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => toggleIssue(i)}
                        className={`rounded-full border px-3 py-1.5 font-mono text-xs transition-colors ${issues.includes(i) ? "border-neon-gold bg-neon-gold/10 text-neon-gold" : "border-white/15 text-slate-400 hover:border-neon-gold/50"}`}>
                        {i}
                      </button>
                    ))}
                  </div>
                  <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="quote-description-input"
                    placeholder="Anything else we should know? When did it start, any drops/spills..."
                    className="mb-4 border-white/10 bg-ink text-white" rows={3} />
                  <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-white/20 p-5 text-sm text-slate-400 transition-colors hover:border-neon-gold/50"
                    data-testid="quote-file-dropzone">
                    <Upload className="h-4 w-4" />
                    {files.length ? files.join(", ") : "Attach photos of the damage (optional)"}
                    <input type="file" multiple accept="image/*" className="hidden" data-testid="quote-file-input"
                      onChange={(e) => setFiles(Array.from(e.target.files).map((f) => f.name))} />
                  </label>
                  <div className="mt-6 flex items-center justify-between">
                    <button onClick={() => setStep(1)} data-testid="quote-step2-back-button"
                      className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase text-slate-400 hover:text-white">
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    <button onClick={() => model ? setStep(3) : toast.error("Tell us the brand & model.")} data-testid="quote-step2-next-button"
                      className="inline-flex items-center gap-2 rounded-md bg-neon-gold px-6 py-3 font-display text-xs font-extrabold uppercase text-ink transition-colors hover:bg-white">
                      See Estimate <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <p className="mb-4 font-mono text-xs uppercase tracking-widest text-slate-400">Step 3 · Your estimate & contact</p>
                  <div className="mb-6 rounded-lg border border-neon-gold/30 bg-neon-gold/5 p-5 font-mono text-sm" data-testid="quote-estimate-panel">
                    <div className="flex justify-between text-slate-300"><span>{device?.id} base service</span><span>${device?.base}</span></div>
                    <div className="flex justify-between text-slate-300"><span>Additional issues ({Math.max(0, issues.length - 1)})</span><span>${Math.max(0, issues.length - 1) * 15}</span></div>
                    <div className="mt-2 flex justify-between border-t border-neon-gold/20 pt-2 text-lg font-bold text-neon-gold">
                      <span>ESTIMATED FROM</span><span data-testid="quote-estimate-total">${estimate}</span>
                    </div>
                    <p className="mt-2 text-[10px] text-slate-500">Final quote confirmed after free in-shop diagnostics. Parts extra where noted.</p>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div>
                      <Label className="text-xs text-slate-400">Name *</Label>
                      <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="quote-name-input" placeholder="Jane Doe" className="border-white/10 bg-ink text-white" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">Email *</Label>
                      <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="quote-email-input" placeholder="you@example.com" className="border-white/10 bg-ink text-white" />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-400">Phone</Label>
                      <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="quote-phone-input" placeholder="860-234-7909" className="border-white/10 bg-ink text-white" />
                    </div>
                  </div>
                  <div className="mt-6 flex items-center justify-between">
                    <button onClick={() => setStep(2)} data-testid="quote-step3-back-button"
                      className="inline-flex items-center gap-2 font-display text-xs font-bold uppercase text-slate-400 hover:text-white">
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    <button onClick={submit} disabled={submitting} data-testid="repair-quote-submit-button"
                      className="inline-flex items-center gap-2 rounded-md bg-neon-gold px-6 py-3 font-display text-xs font-extrabold uppercase text-ink transition-colors hover:bg-white disabled:opacity-60">
                      <Wrench className="h-4 w-4" /> {submitting ? "Sending..." : "Request Quote"}
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
